/**
 * Slice 0 — Scenario as a hard scope container.
 * Bound Input Scope is restored on activate; live escape is forbidden while bound.
 */

import type { Scenario } from './planningEngineModule.types';
import type { EngineInputSource } from './planningEngineService';

const BOUND_TYPES = new Set<EngineInputSource['type']>([
  'item_pool',
  'collection',
  'published_priority_list',
  'queue',
  'scenario_portfolio',
]);

/** True when the scenario has a persisted/derived non-live Input Scope. */
export function scenarioHasHardScope(
  scenario: Pick<Scenario, 'inputSourceType' | 'inputSourceId'> | null | undefined,
): boolean {
  if (!scenario?.inputSourceType) return false;
  if (!BOUND_TYPES.has(scenario.inputSourceType as EngineInputSource['type'])) return false;
  // scenario_portfolio uses scenario id as source id; other types need an id
  if (scenario.inputSourceType === 'scenario_portfolio') return true;
  return Boolean(scenario.inputSourceId);
}

/** Reject switching to All Live Items while the scenario is a hard scope container. */
export function mayUseLiveEngineInput(
  scenario: Pick<Scenario, 'inputSourceType' | 'inputSourceId'> | null | undefined,
): boolean {
  return !scenarioHasHardScope(scenario);
}

export function emptyScopeExplanation(args: {
  surface: 'work_units' | 'priorities' | 'dependencies' | 'generic';
  planningMode?: string | null;
  scopeLabel?: string;
  scopedCount: number;
  totalCount: number;
}): { title: string; description: string } {
  const { surface, planningMode, scopeLabel, scopedCount, totalCount } = args;
  if (totalCount > 0 && scopedCount === 0) {
    return {
      title: `Scope shows 0 of ${totalCount}`,
      description: `This scenario’s Input Scope${scopeLabel ? ` (${scopeLabel})` : ''} has no matching ${surface.replace('_', ' ')}. Switch scenario or change the bound pool — All Live Items is locked while the scenario is hard-scoped.`,
    };
  }
  if (surface === 'work_units' && (planningMode === 'priority_estimate' || planningMode === 'priority-estimate')) {
    return {
      title: 'No work units in this scenario',
      description:
        'This scenario is priority-estimate — Launch Calendar places priorities directly. Work units appear when the scenario uses work-unit planning mode or you sync WUs from items.',
    };
  }
  return {
    title: `No ${surface.replace('_', ' ')} yet`,
    description: 'Nothing in this scenario’s hard scope yet. Add items to the bound pool or run placement.',
  };
}
